import { useTranslation } from 'react-i18next';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { StatusBadge } from '@/components/common/StatusBadge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  MapPin, FolderKanban, Wrench, List, Search, FileText,
  Navigation, UserPlus, FilePlus, X, Loader2, Building2, Landmark, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import { contactPrimaryLabel } from '@/lib/contactDisplay';
import { stateNameToCode } from '@/constants/usStates';
import 'leaflet/dist/leaflet.css';

// Title-case a single word (for owner names returned ALL CAPS by county records)
function toTitle(word) {
  return word ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase() : word;
}

// Haversine distance in miles
function haversineDistance(lat1, lng1, lat2, lng2) {
  const R = 3958.8;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

const OFFICE = { lat: 27.4958, lng: -81.4411, label: 'Blue Well Drilling', address: '3307 Baxter Ave, Sebring, FL 33870' };

const COLORS = {
  office:   '#1E40AF',
  project:  '#003366',
  job:      '#3399FF',
  estimate: '#F97316',
  search:   '#8B5CF6',
};

// Alian (video "Ajustes de colores y estados en mapa"): estimate pins must use
// the SAME status palette as the Estimates list (StatusBadge dot colors), not
// one fixed color for every estimate regardless of draft/sent/approved/etc.
const ESTIMATE_STATUS_COLORS = {
  draft:                '#94a3b8', // slate-400
  sent:                 '#3b82f6', // blue-500
  viewed:               '#0ea5e9', // sky-500
  accepted:             '#10b981', // emerald-500 (Approved)
  rejected:             '#ef4444', // red-500
  converted_to_invoice: '#2563eb', // blue-600 (Invoiced)
  converted_to_job:     '#8b5cf6', // violet-500 (Converted)
};
const ESTIMATE_STATUS_LABELS = {
  draft: 'Draft',
  sent: 'Sent',
  viewed: 'Viewed',
  accepted: 'Approved',
  rejected: 'Rejected',
  converted_to_invoice: 'Invoiced',
  converted_to_job: 'Converted',
};
function estimateStatusColor(status) {
  return ESTIMATE_STATUS_COLORS[status] || COLORS.estimate;
}
function estimateStatusLabel(status) {
  return ESTIMATE_STATUS_LABELS[status] || status || 'Draft';
}
function hexToRgba(hex, alpha) {
  const h = (hex || '').replace('#', '');
  const r = parseInt(h.substring(0, 2), 16) || 0;
  const g = parseInt(h.substring(2, 4), 16) || 0;
  const b = parseInt(h.substring(4, 6), 16) || 0;
  return `rgba(${r},${g},${b},${alpha})`;
}

function makeIcon(L, color, svgInner, size = 24) {
  return L.divIcon({
    html: `<div style="background:${color};width:${size}px;height:${size}px;border-radius:50%;display:flex;align-items:center;justify-content:center;border:2px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.35)">${svgInner}</div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    className: '',
  });
}

export default function MapsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const layersRef = useRef({ office: null, projects: null, jobs: null, estimates: null, search: null });

  const [projects, setProjects] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [estimates, setEstimates] = useState([]);
  const [contactsById, setContactsById] = useState({});
  const [loading, setLoading] = useState(true);
  const [showList, setShowList] = useState(false);

  // Filter toggles
  const [filters, setFilters] = useState({ office: true, projects: true, jobs: true, estimates: true });

  // Address search state
  const [searchAddress, setSearchAddress] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResult, setSearchResult] = useState(null); // { lat, lng, address, city, county, zip, distance, ownerName, parcelId }
  const [propertyLoading, setPropertyLoading] = useState(false);
  const [appraiserLink, setAppraiserLink] = useState(null);

  // ---- Load data ----
  useEffect(() => {
    Promise.all([
      api.get('/projects').catch(() => ({ data: [] })),
      api.get('/jobs', { params: { page_size: 500 } }).catch(() => ({ data: { items: [] } })),
      api.get('/estimates', { params: { page_size: 500 } }).catch(() => ({ data: { items: [] } })),
      api.get('/contacts', { params: { page_size: 500 } }).catch(() => ({ data: { items: [] } })),
    ]).then(([projRes, jobsRes, estRes, contactsRes]) => {
      setProjects(projRes.data || []);
      setJobs(jobsRes.data.items || jobsRes.data || []);
      setEstimates(estRes.data.items || estRes.data || []);
      const contacts = contactsRes.data.items || contactsRes.data || [];
      const map = {};
      contacts.forEach(c => { map[c.id] = c; });
      setContactsById(map);
      setLoading(false);
    });
  }, []);

  // Alian (video D): map pins must show client/address/type/status on HOVER,
  // without a click — falls back to the record's own title when there's no
  // matching contact (e.g. old records without customer_id).
  const clientNameFor = useCallback((record) => {
    const contact = contactsById[record.customer_id];
    return (contact ? contactPrimaryLabel(contact) : '') || record.title || '';
  }, [contactsById]);

  // ---- Build / rebuild map ----
  useEffect(() => {
    if (loading || !mapRef.current) return;
    let isMounted = true;

    const initMap = async () => {
      // Cleanup any existing instance
      if (mapInstanceRef.current) {
        try { mapInstanceRef.current.remove(); } catch {}
        mapInstanceRef.current = null;
      }
      // Also clear the DOM node's leaflet ID (handles React StrictMode double-invoke)
      if (mapRef.current) {
        delete mapRef.current._leaflet_id;
        mapRef.current.innerHTML = '';
      }
      if (!isMounted || !mapRef.current) return;

      const L = await import('leaflet');
      if (!isMounted || !mapRef.current) return;

      const map = L.map(mapRef.current).setView([OFFICE.lat, OFFICE.lng], 9);
      mapInstanceRef.current = map;

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap contributors',
      }).addTo(map);

      // ---- Icons ----
      const iconOffice = makeIcon(L, COLORS.office, '<span style="font-size:14px">&#127754;</span>', 28);
      const iconProject = makeIcon(L, COLORS.project, '<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5"><path d="M3 3h7l2 2h9v14H3z"/></svg>');
      const iconJob = makeIcon(L, COLORS.job, '<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5"><path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z"/></svg>');
      // Estimate pin icon color/label are computed per-marker below (status-based).
      const ESTIMATE_SVG = '<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14,2 14,8 20,8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>';

      // ---- Layer groups ----
      const officeLayer = L.layerGroup();
      const projectLayer = L.layerGroup();
      const jobLayer = L.layerGroup();
      const estimateLayer = L.layerGroup();

      layersRef.current = { office: officeLayer, projects: projectLayer, jobs: jobLayer, estimates: estimateLayer, search: null };

      // Office marker
      L.marker([OFFICE.lat, OFFICE.lng], { icon: iconOffice })
        .addTo(officeLayer)
        .bindPopup(`<div style="font-family:Barlow,sans-serif;min-width:160px"><strong style="color:#003366">Blue Well Drilling</strong><br/><span style="font-size:12px;color:#555">${OFFICE.address}</span></div>`);

      // Project markers
      projects.forEach(p => {
        const coords = p.location?.coordinates || p.location;
        const lat = coords?.lat;
        const lng = coords?.lng;
        if (lat && lng) {
          const tooltip = `<div style="font-family:Barlow,sans-serif;min-width:180px">
            <div style="font-weight:700;color:#003366;margin-bottom:4px">${p.project_number || ''} · <span style="font-weight:600;font-size:11px;color:#003366">Project</span></div>
            <div style="font-size:13px;margin-bottom:2px">${clientNameFor(p)}</div>
            <div style="font-size:11px;color:#666;margin-bottom:6px">${p.location?.address || ''} ${p.location?.city || ''} ${p.location?.county ? '· ' + p.location.county + ' County' : ''}</div>
            <span style="display:inline-block;background:#e5e7eb;color:#374151;font-size:11px;padding:1px 8px;border-radius:999px">${p.status || ''}</span>
          </div>`;
          L.marker([lat, lng], { icon: iconProject }).addTo(projectLayer)
            .bindTooltip(tooltip, { direction: 'top', offset: [0, -8], opacity: 0.98, className: 'bw-map-tooltip' })
            .on('click', () => navigate(`/projects/${p.id}`));
        }
      });

      // Job markers
      jobs.forEach(j => {
        const coords = j.location?.coordinates || j.location;
        const lat = coords?.lat;
        const lng = coords?.lng;
        if (lat && lng) {
          const tooltip = `<div style="font-family:Barlow,sans-serif;min-width:180px">
            <div style="font-weight:700;color:#3399FF;margin-bottom:4px">${j.job_number || ''} · <span style="font-weight:600;font-size:11px;color:#3399FF">Job</span></div>
            <div style="font-size:13px;margin-bottom:2px">${clientNameFor(j)}</div>
            <div style="font-size:11px;color:#666;margin-bottom:6px">${j.well_address || j.location?.address || ''} ${j.location?.county ? '· ' + j.location.county + ' County' : ''}</div>
            <span style="display:inline-block;background:#e5e7eb;color:#374151;font-size:11px;padding:1px 8px;border-radius:999px">${j.status || ''}</span>
          </div>`;
          L.marker([lat, lng], { icon: iconJob }).addTo(jobLayer)
            .bindTooltip(tooltip, { direction: 'top', offset: [0, -8], opacity: 0.98, className: 'bw-map-tooltip' })
            .on('click', () => navigate(`/jobs/${j.id}`));
        }
      });

      // Estimate markers — color + label come from the estimate's real status
      // (Alian: "me gustaría que fuera el aprobado... y lo que está convertido
      // para el trabajo", same palette as the Estimates list status badges).
      estimates.forEach(e => {
        const lat = e.well_coordinates?.lat;
        const lng = e.well_coordinates?.lng;
        if (lat && lng) {
          const color = estimateStatusColor(e.status);
          const label = estimateStatusLabel(e.status);
          const iconEstimate = makeIcon(L, color, ESTIMATE_SVG);
          const tooltip = `<div style="font-family:Barlow,sans-serif;min-width:180px">
            <div style="font-weight:700;color:${color};margin-bottom:4px">${e.estimate_number || ''} · <span style="font-weight:600;font-size:11px;color:${color}">${label}</span></div>
            <div style="font-size:13px;margin-bottom:2px">${clientNameFor(e)}</div>
            <div style="font-size:11px;color:#666;margin-bottom:6px">${e.well_address || ''}</div>
            <span style="display:inline-block;background:${hexToRgba(color, 0.12)};color:${color};font-size:11px;padding:1px 8px;border-radius:999px">${label}</span>
          </div>`;
          L.marker([lat, lng], { icon: iconEstimate }).addTo(estimateLayer)
            .bindTooltip(tooltip, { direction: 'top', offset: [0, -8], opacity: 0.98, className: 'bw-map-tooltip' })
            .on('click', () => navigate('/estimates', { state: { openEstimateId: e.id } }));
        }
      });

      // Focus marker — Office Workflow "Work Site Location" deep-link
      // (Alian: clickeable, abre el mapa en esa ubicacion). Query params:
      // ?lat=..&lng=..&address=..&label=..
      const focusLat = parseFloat(searchParams.get('lat'));
      const focusLng = parseFloat(searchParams.get('lng'));
      if (!isNaN(focusLat) && !isNaN(focusLng)) {
        const focusAddress = searchParams.get('address') || '';
        const focusLabel = searchParams.get('label') || 'Work Site Location';
        const iconFocus = makeIcon(L, COLORS.search, '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5"><path d="M12 21s-7-6.5-7-11a7 7 0 0114 0c0 4.5-7 11-7 11z"/><circle cx="12" cy="10" r="2.5"/></svg>', 30);
        const focusMarker = L.marker([focusLat, focusLng], { icon: iconFocus }).addTo(map)
          .bindPopup(`<div style="font-family:Barlow,sans-serif;min-width:180px"><strong style="color:#8B5CF6">${focusLabel}</strong><br/><span style="font-size:12px;color:#555">${focusAddress}</span></div>`);
        map.setView([focusLat, focusLng], 16);
        setTimeout(() => focusMarker.openPopup(), 300);
      }

      // Add layers based on current filter state
      if (filters.office) officeLayer.addTo(map);
      if (filters.projects) projectLayer.addTo(map);
      if (filters.jobs) jobLayer.addTo(map);
      if (filters.estimates) estimateLayer.addTo(map);

      setTimeout(() => map.invalidateSize(), 100);
    };

    initMap();

    return () => {
      isMounted = false;
      if (mapInstanceRef.current) {
        try { mapInstanceRef.current.stop(); } catch {}
        try { mapInstanceRef.current.remove(); } catch {}
        mapInstanceRef.current = null;
      }
    };
  }, [loading, projects, jobs, estimates, contactsById]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Toggle filter layers ----
  useEffect(() => {
    const map = mapInstanceRef.current;
    const layers = layersRef.current;
    if (!map || !layers.office) return;
    const toggle = (layer, show) => {
      if (!layer) return;
      if (show && !map.hasLayer(layer)) layer.addTo(map);
      if (!show && map.hasLayer(layer)) map.removeLayer(layer);
    };
    toggle(layers.office, filters.office);
    toggle(layers.projects, filters.projects);
    toggle(layers.jobs, filters.jobs);
    toggle(layers.estimates, filters.estimates);
  }, [filters]);

  // ---- Address search ----
  const handleAddressSearch = useCallback(async () => {
    if (!searchAddress.trim()) return;
    setSearchLoading(true);
    setSearchResult(null);
    try {
      const params = new URLSearchParams({ address: searchAddress, state: 'Florida' });
      const res = await api.get(`/geocode/address?${params}`);
      if (res.data?.success && res.data.lat && res.data.lng) {
        const dist = haversineDistance(OFFICE.lat, OFFICE.lng, res.data.lat, res.data.lng);
        const details = res.data.address_details || {};
        const result = {
          lat: res.data.lat,
          lng: res.data.lng,
          address: res.data.address || searchAddress,
          city: details.city ? details.city.charAt(0) + details.city.slice(1).toLowerCase() : '',
          county: details.county || '',
          zip: details.postcode || '',
          state: stateNameToCode(details.state) || 'FL',
          distance: dist.toFixed(1),
          ownerName: '',
          parcelId: '',
        };
        setSearchResult(result);
        // Alian's video request: pull the legal Property Owner + Parcel ID
        // automatically from county records via spatial (point-in-polygon)
        // lookup — no more manual trips to each county's Property Appraiser.
        setPropertyLoading(true);
        api.get('/property/by-coordinates', { params: { lat: result.lat, lng: result.lng, county: result.county || '', address: result.address || '' } })
          .then(propRes => {
            const prop = propRes.data;
            if (prop && (prop.owner_name || prop.parcel_id)) {
              setSearchResult(prev => prev && {
                ...prev,
                ownerName: prop.owner_name || '',
                parcelId: prop.parcel_id || '',
                county: prop.property_county?.replace(' County', '') || prev.county,
              });
            }
          })
          .catch(() => {})
          .finally(() => setPropertyLoading(false));
        // Add search pin to map
        const map = mapInstanceRef.current;
        if (map) {
          const L = await import('leaflet');
          // Remove previous search pin
          if (layersRef.current.search) map.removeLayer(layersRef.current.search);
          const icon = makeIcon(L, COLORS.search, '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>', 28);
          const marker = L.marker([result.lat, result.lng], { icon })
            .bindPopup(`<div style="font-family:Barlow,sans-serif;min-width:160px">
              <strong style="color:#8B5CF6">Address searched</strong><br/>
              <span style="font-size:12px">${result.address}</span><br/>
              <span style="font-size:12px;color:#666">${result.city}${result.zip ? ', ' + (result.state || 'FL') + ' ' + result.zip : ''}${result.county ? ' · ' + result.county + ' County' : ''}</span><br/>
              <strong style="font-size:12px;color:#003366">${result.distance} mi from office</strong>
            </div>`)
            .addTo(map)
            .openPopup();
          layersRef.current.search = marker;
          map.setView([result.lat, result.lng], 11);
        }
      } else {
        toast.error('Could not find the address. Try being more specific.');
      }
    } catch {
      toast.error('Error searching for the address.');
    } finally {
      setSearchLoading(false);
    }
  }, [searchAddress]);

  // Fallback: when no owner/parcel was found for the searched point, offer a
  // direct link to that county's own Property Appraiser site (Alian keeps
  // these as browser bookmarks today — this replaces that manual step).
  useEffect(() => {
    if (!searchResult || propertyLoading) { setAppraiserLink(null); return; }
    if (searchResult.ownerName || searchResult.parcelId || !searchResult.county) { setAppraiserLink(null); return; }
    api.get('/property/appraiser-link', { params: { county: searchResult.county } })
      .then(res => setAppraiserLink(res.data))
      .catch(() => setAppraiserLink(null));
  }, [searchResult, propertyLoading]);

  const clearSearch = useCallback(async () => {
    setSearchResult(null);
    setSearchAddress('');
    setAppraiserLink(null);
    const map = mapInstanceRef.current;
    if (map && layersRef.current.search) {
      map.removeLayer(layersRef.current.search);
      layersRef.current.search = null;
      map.setView([OFFICE.lat, OFFICE.lng], 9);
    }
  }, []);

  // ---- Create actions from search result ----
  const handleCreateCustomer = () => {
    if (!searchResult) return;
    // Split "LASTNAME FIRSTNAME MIDDLE" (county appraiser convention) into
    // first/last name so the new contact opens pre-filled with the legal
    // owner — Alian's request: "que ya uno cree el cliente y tome esa info".
    let firstName = '', lastName = '';
    if (searchResult.ownerName) {
      const parts = searchResult.ownerName.trim().split(/\s+/);
      lastName = parts[0] ? toTitle(parts[0]) : '';
      firstName = parts.slice(1).map(toTitle).join(' ');
    }
    navigate('/contacts', {
      state: {
        fromMap: true,
        address: searchResult.address,
        city: searchResult.city,
        county: searchResult.county,
        stateVal: searchResult.state,
        zip: searchResult.zip,
        firstName,
        lastName,
        parcelId: searchResult.parcelId,
      }
    });
  };

  const handleCreateEstimate = () => {
    if (!searchResult) return;
    navigate('/estimates', {
      state: {
        fromMap: true,
        wellAddress: `${searchResult.address}, ${searchResult.city}, ${searchResult.state}`,
        wellCoordinates: { lat: searchResult.lat, lng: searchResult.lng },
        city: searchResult.city,
        zip: searchResult.zip,
        state: searchResult.state,
        county: searchResult.county,
        ownerName: searchResult.ownerName,
        parcelId: searchResult.parcelId,
      }
    });
  };

  // ---- List view data ----
  const allItems = [
    ...projects.filter(p => (p.location?.coordinates?.lat || p.location?.lat)).map(p => ({ ...p, _type: 'project' })),
    ...jobs.filter(j => (j.location?.coordinates?.lat || j.location?.lat)).map(j => ({ ...j, _type: 'job' })),
    ...estimates.filter(e => e.well_coordinates?.lat).map(e => ({ ...e, _type: 'estimate' })),
  ];

  const FilterBtn = ({ id, label, color, icon: Icon }) => (
    <button
      data-testid={`filter-${id}`}
      onClick={() => setFilters(f => ({ ...f, [id]: !f[id] }))}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
        filters[id]
          ? 'text-white border-transparent shadow-sm'
          : 'bg-white text-gray-500 border-gray-200 opacity-60'
      }`}
      style={filters[id] ? { background: color } : {}}
    >
      <div className="w-2 h-2 rounded-full" style={{ background: filters[id] ? 'rgba(255,255,255,0.8)' : color }} />
      {Icon && <Icon className="w-3 h-3" />}
      {label}
    </button>
  );

  return (
    <div data-testid="maps-page" className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 font-['Barlow'] tracking-tight flex items-center gap-2">
            <MapPin className="w-6 h-6 text-[#3399FF]" /> {t('maps.title')}
          </h1>
          <p className="text-sm text-gray-500 mt-1">{t('maps.subtitle')}</p>
        </div>
        <Button
          variant={showList ? 'default' : 'outline'}
          size="sm"
          onClick={() => setShowList(!showList)}
          className={showList ? 'bg-[#003366] text-white' : ''}
          data-testid="toggle-list-view"
        >
          <List className="w-4 h-4 mr-1" /> {t('maps.listView')}
        </Button>
      </div>

      {/* Filter toggles */}
      <div className="flex flex-wrap gap-2">
        <FilterBtn id="office"    label="Office"    color={COLORS.office}    icon={Building2} />
        <FilterBtn id="projects"  label="Projects"  color={COLORS.project}   icon={FolderKanban} />
        <FilterBtn id="jobs"      label="Jobs"       color={COLORS.job}       icon={Wrench} />
        <FilterBtn id="estimates" label="Estimates"  color={COLORS.estimate}  icon={FileText} />
      </div>

      {/* Address Search Bar */}
      <Card className="bg-white border-gray-100 shadow-sm">
        <CardContent className="p-3">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1">
            <Navigation className="w-3 h-3" /> Quick address search
          </p>
          <div className="flex gap-2">
            <div className="flex-1">
              <Input
                data-testid="address-search-input"
                placeholder="e.g. 221 Vienna Ave, Lake Placid, FL..."
                value={searchAddress}
                onChange={e => setSearchAddress(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleAddressSearch()}
                className="h-9 text-sm"
              />
            </div>
            <Button
              data-testid="address-search-btn"
              size="sm"
              onClick={handleAddressSearch}
              disabled={searchLoading || !searchAddress.trim()}
              className="bg-[#003366] hover:bg-[#004080] text-white h-9"
            >
              {searchLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              <span className="ml-1 hidden sm:inline">Search</span>
            </Button>
          </div>

          {/* Search Result Card */}
          {searchResult && (
            <div data-testid="search-result-card" className="mt-3 p-3 rounded-lg border border-purple-100 bg-purple-50 flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <MapPin className="w-4 h-4 text-purple-600 flex-shrink-0" />
                  <span className="font-medium text-sm text-gray-900 truncate">{searchResult.address}</span>
                  {(searchResult.city || searchResult.zip) && <span className="text-xs text-gray-500">{searchResult.city}{searchResult.zip ? `, ${searchResult.state || 'FL'} ${searchResult.zip}` : ''}{searchResult.county ? ` · ${searchResult.county} County` : ''}</span>}
                </div>
                <div className="flex items-center gap-3 mt-1.5">
                  <Badge variant="outline" className="text-xs border-purple-200 text-purple-700 bg-white">
                    <Navigation className="w-3 h-3 mr-1" />
                    {searchResult.distance} mi from office
                  </Badge>
                  {parseFloat(searchResult.distance) <= 30 && (
                    <span className="text-xs text-emerald-600 font-medium">Within service area</span>
                  )}
                  {parseFloat(searchResult.distance) > 30 && parseFloat(searchResult.distance) <= 60 && (
                    <span className="text-xs text-amber-600 font-medium">Extended zone</span>
                  )}
                  {parseFloat(searchResult.distance) > 60 && (
                    <span className="text-xs text-red-500 font-medium">Outside usual area</span>
                  )}
                </div>
                {/* Owner / Parcel auto-lookup (Alian's video 7:00 — pull the legal
                    owner + parcel from county records instead of manual lookup) */}
                <div className="mt-1.5" data-testid="map-property-result">
                  {propertyLoading ? (
                    <span className="text-xs text-gray-400 flex items-center gap-1">
                      <Loader2 className="w-3 h-3 animate-spin" /> Looking up legal owner…
                    </span>
                  ) : (searchResult.ownerName || searchResult.parcelId) ? (
                    <div className="flex items-center gap-2 flex-wrap text-xs text-[#003366]" data-testid="map-owner-found">
                      <Landmark className="w-3.5 h-3.5 text-[#003366]" />
                      {searchResult.ownerName && <span className="font-semibold">{searchResult.ownerName}</span>}
                      {searchResult.parcelId && <span className="font-mono text-gray-500">· {searchResult.parcelId}</span>}
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 flex-wrap text-xs text-gray-500" data-testid="map-owner-not-found">
                      <span>No owner/parcel found for this point.</span>
                      {appraiserLink && (
                        <a href={appraiserLink.url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline flex items-center gap-0.5" data-testid="map-appraiser-link">
                          Property Appraiser {appraiserLink.county && `(${appraiserLink.county})`} <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Button
                  data-testid="create-customer-from-map-btn"
                  size="sm"
                  variant="outline"
                  onClick={handleCreateCustomer}
                  title="Creates a contact with the property owner's info (county records) — the estimate's customer can be a different person"
                  className="h-8 text-xs border-[#003366] text-[#003366] hover:bg-[#003366] hover:text-white"
                >
                  <UserPlus className="w-3.5 h-3.5 mr-1" /> Create Customer (Owner)
                </Button>
                <Button
                  data-testid="create-estimate-from-map-btn"
                  size="sm"
                  onClick={handleCreateEstimate}
                  className="h-8 text-xs bg-[#003366] hover:bg-[#004080] text-white"
                >
                  <FilePlus className="w-3.5 h-3.5 mr-1" /> Create Estimate
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={clearSearch}
                  className="h-8 w-8 p-0 text-gray-400 hover:text-gray-600"
                  data-testid="clear-search-btn"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Map + List */}
      <div className={`grid ${showList ? 'grid-cols-1 lg:grid-cols-3' : 'grid-cols-1'} gap-6`}>
        <Card className={`bg-white border-gray-100 shadow-sm overflow-hidden ${showList ? 'lg:col-span-2' : ''}`}>
          <CardContent className="p-0">
            {loading ? (
              <div className="h-[480px] flex items-center justify-center text-gray-400">
                <Loader2 className="w-6 h-6 animate-spin mr-2" /> {t('maps.loadingMap')}
              </div>
            ) : (
              <div ref={mapRef} data-testid="map-container" className="h-[480px] w-full" />
            )}
          </CardContent>
        </Card>

        {showList && (
          <Card className="bg-white border-gray-100 shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold font-['Barlow']">
                {t('maps.locations', { count: allItems.length })}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0 max-h-[420px] overflow-y-auto space-y-2">
              {allItems.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-8">{t('maps.noCounty')}</p>
              ) : (
                allItems.map((item, i) => (
                  <div
                    key={i}
                    data-testid={`map-list-item-${i}`}
                    className="p-3 rounded-lg border border-gray-100 hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => {
                      const coords = item._type === 'estimate'
                        ? { lat: item.well_coordinates?.lat, lng: item.well_coordinates?.lng }
                        : { lat: item.location?.coordinates?.lat || item.location?.lat, lng: item.location?.coordinates?.lng || item.location?.lng };
                      if (coords?.lat && mapInstanceRef.current) {
                        mapInstanceRef.current.setView([coords.lat, coords.lng], 13);
                      }
                    }}
                  >
                    <div className="flex items-center gap-2">
                      {item._type === 'project' && <FolderKanban className="w-4 h-4 text-[#003366] flex-shrink-0" />}
                      {item._type === 'job'     && <Wrench      className="w-4 h-4 text-[#3399FF] flex-shrink-0" />}
                      {item._type === 'estimate'&& <FileText    className="w-4 h-4 text-[#F97316] flex-shrink-0" />}
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{item.title}</p>
                        <p className="text-xs text-gray-400">
                          {item.project_number || item.job_number || item.estimate_number}
                          {(item.location?.county || item.well_address) && ' · '}
                          {item.location?.county || (item._type === 'estimate' ? item.well_address?.split(',')[0] : '')}
                        </p>
                      </div>
                    </div>
                    <div className="mt-1.5">
                      <StatusBadge status={item.status} />
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 text-xs text-gray-500" data-testid="map-legend">
        <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-full" style={{ background: COLORS.office }} />Office</div>
        <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-full" style={{ background: COLORS.project }} />Projects</div>
        <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-full" style={{ background: COLORS.job }} />Jobs</div>
        <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-full" style={{ background: COLORS.search }} />Search</div>
      </div>
      {/* Estimate status legend — pins are colored by status, not by type
          (Alian: "ese mismo estatus [de la lista de estimados], ponéselo al mapa") */}
      <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500" data-testid="estimate-status-legend">
        <span className="font-semibold text-gray-400 uppercase tracking-wide text-[10px]">Estimate status:</span>
        {Object.keys(ESTIMATE_STATUS_LABELS).map(key => (
          <div key={key} className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-full" style={{ background: ESTIMATE_STATUS_COLORS[key] }} />
            {ESTIMATE_STATUS_LABELS[key]}
          </div>
        ))}
      </div>
    </div>
  );
}
